import moment from "moment";
import { companyToday } from "../../helpers/companyTime";

import { call, takeEvery, put, select, all, fork } from "redux-saga/effects";
import { types } from "./types";
import { types as workspaceTypes } from "../workspace/types";
import { types as claimsType } from "../claims/types";
import startCase from "lodash/startCase";
import cloneDeep from "lodash/cloneDeep";
import { isRateLikeKpi, isWindowRateKpi, isBidAccuracyKpi, isBidAccuracyCell } from "../../helpers/scoreAggregate";
import { applyShortWindowRates } from "../../helpers/windowRates";

import {
  GET_GROUP_KPIS,
  UPDATE_GROUP_KPI,
  CREATE_CUSTOM_KPI,
  DISABLE_GROUP_KPI,
  GET_ALL_USER_KPIS,
  ADD_USER_KPI,
  UPDATE_USER_KPI,
  DELETE_USER_KPI,
} from "../../api/kpi";
import { ADD_INTEGRATION } from "../../api/integration";
import { GET_LINKS } from "../../api/links";

const getCompanyId = (state) => state.authReducer.userDetails.companyId;
const getUserId = (state) => state.authReducer.userDetails._id;
const getIsAdmin = (state) => state.authReducer.userDetails.isAdmin;
const getSelectedWorkspace = (state) =>
  state.workspacesReducer.selectedWorkspace;
const getKpiMonthRef = (state) => state.kpiReducer.monthRef;
const getHistoricKpis = (state) => state.kpiReducer.historicKpis;
const getMyKpis = (state) => state.kpiReducer.myKpis;
const getUsersKpis = (state) => state.kpiReducer.usersKpis;

const addKpiObjects = (kpiSum, plusKpi, kpiMetaById = null, day = null) => {
  Object.keys(plusKpi).forEach((key) => {
    if (key === "total") return;
    const incoming = plusKpi[key];
    if (!incoming) return;
    const meta = kpiMetaById?.[key];
    const calc = meta?.calc || meta?.parameters?.calc || incoming.calc;
    const isRateLike = isRateLikeKpi(meta, { ...incoming, calc });
    if (kpiSum[key]) {
      if (isRateLike) {
        const have = kpiSum[key].__day;
        if (day && (!have || String(day) < String(have))) {
          kpiSum[key] = { ...incoming, __day: day };
        }
      } else {
        kpiSum[key].pts += incoming?.pts || 0;
        kpiSum[key].amt += incoming?.amt || 0;
      }
    } else {
      kpiSum[key] = isRateLike && day ? { ...incoming, __day: day } : { ...incoming };
    }
    if (!isRateLike) {
      kpiSum.total += incoming?.pts ?? 0;
    }
  });
  return kpiSum;
};

function* RefreshKpis() {
  yield put({ type: types.REFRESH_KPIS });
}

function* GetKpis() {
  try {
    const companyId = yield select(getCompanyId);
    const userId = yield select(getUserId);
    const isAdmin = yield select(getIsAdmin);
    const workspace = yield select(getSelectedWorkspace);


    // Workspace may not be selected yet during startup — don't wipe KPI state.
    if (!workspace?._id) {
      yield put({ type: types.KPI_FETCH_SKIPPED });
      return;
    }

    // includeRetired: also return long-disabled metrics (tagged `retired`) so
    // Past Boards can keep rendering them for the months they had scores. They
    // are kept out of the live catalog below.
    const targetKpis = yield call(() => GET_GROUP_KPIS(companyId, null, true));

    if (targetKpis.data && !targetKpis.data.length) {
      yield put({ type: types.GET_KPI_SCORE_EMPTY });
      return;
    }

    const allCompanyKpis = targetKpis.data
      .map(
        ({
          _id,
          name,
          pointScale,
          pointValue,
          target,
          requiredInputs,
          model,
          groupId,
          linkedGroupIds,
          parameters,
          sourceId,
          source,
          integration,
          integrationId,
          integrationMeta,
          isAdminAccess,
          metricType,
          optional,
          isDraft,
          disabled,
          retired,
          appearance,
          chartGranularity,
          guideText,
          dailyGoalText,
        }) => ({
          _id,
          groupId,
          linkedGroupIds,
          metricType,
          optional,
          isDraft,
          disabled,
          retired,
          pointScale: pointScale ?? model.defaultPointScale,
          ...(pointValue != null && { pointValue }),
          defaultPointScale: model.defaultPointScale,
          target: target ?? model.defaultTarget,
          title: startCase(name ?? model.type),
          kind: model.kind,
          type: model.type,
          kpiModelId: model._id,
          direction: model.direction,
          isManual: !sourceId,
          isShared: !!(parameters?.level || source?.parameters?.level),
          isCustom: model.isTemplate === true,
          isAdminAccess: isAdminAccess ?? false,
          integrationId,
          ...(integrationMeta && { integrationMeta }),
          sourceId,
          source: integration?.code,
          sourceName: integration?.name,
          // Integration "calc" types that are period snapshots (rate, average,
          // latest) stay flat for the whole month. Counting types that build up
          // over the month must NOT — "sum" and "memberships" both track a
          // per-working-day target that ramps with elapsed weekdays.
          wholeMonth:
            source?.parameters?.calc &&
            source?.parameters?.calc !== "sum" &&
            source?.parameters?.calc !== "memberships",
          calc: source?.parameters?.calc,
          gap: source?.parameters?.gap,
          minimum: parameters?.minimum ?? 0,
          parameters: parameters ?? null,
          requiredInputs: requiredInputs && requiredInputs.length > 0 ? requiredInputs : (model.defaultInputs ?? null),
          ...(appearance !== undefined && { appearance }),
          ...(chartGranularity !== undefined && { chartGranularity }),
          ...(typeof guideText === "string" && { guideText }),
          ...(typeof dailyGoalText === "string" && { dailyGoalText }),
        }),
      )
      .sort((a, b) => b.pointScale * b.target - a.pointScale * a.target);

    // Separate draft KPIs at company level
    const draftKpis = allCompanyKpis.filter(kpi => kpi.isDraft);

    // companyKpis is the LIVE catalog: active + recently-disabled (non-retired).
    // companyAllKpis additionally carries retired metrics so historic boards can
    // still render them. Keeping companyKpis retired-free preserves the current
    // board / Company Metrics behavior unchanged.
    const companyKpis = {};
    const companyAllKpis = {};
    const addToBucket = (bucket, workspaceId, groupKpi) => {
      if (!workspaceId) return;
      if (bucket[workspaceId]) bucket[workspaceId].push(groupKpi);
      else bucket[workspaceId] = [groupKpi];
    };
    allCompanyKpis.filter(kpi => !kpi.isDraft).forEach((groupKpi) => {
      // A metric assigned to more than one workspace via "Assign to
      // workspace(s)" carries the SAME document/kpiId into every board it's
      // linked to (see GroupKpi.linkedGroupIds) — not a copy, so scores
      // written under this one kpiId (e.g. by the HCP hours sync) render
      // correctly on every board it appears on.
      // Dedupe defensively: if linkedGroupIds ever contains the doc's own
      // groupId (shouldn't happen server-side, but this is the render path —
      // don't let stray data double-render one card on the same board).
      const boardIds = [...new Set([groupKpi.groupId, ...(groupKpi.linkedGroupIds || [])].map(String))];
      boardIds.forEach((id) => addToBucket(companyAllKpis, id, groupKpi));
      if (groupKpi.retired) return;
      boardIds.forEach((id) => addToBucket(companyKpis, id, groupKpi));
    });

    let groupKpis = [];
    let groupAllKpis = [];
    if (isAdmin) {
      const links = yield call(() => GET_LINKS(companyId));
      const applyLinks = (arr) =>
        (arr ?? []).map((groupKpi) => {
          const index = links.data.data.findIndex(
            (link) => link.kpiId === groupKpi._id,
          );
          return index !== -1
            ? { ...groupKpi, kpiLinks: links.data.data[index] }
            : groupKpi;
        });
      groupKpis = applyLinks(companyKpis[workspace._id]);
      groupAllKpis = applyLinks(companyAllKpis[workspace._id]);
    } else {
      groupKpis = companyKpis[workspace._id] ?? [];
      // Past Boards columns + scoped points recompute read from groupAllKpis;
      // must be populated for players too, not just admins. Includes retired
      // metrics so their history keeps rendering on previous months.
      groupAllKpis = companyAllKpis[workspace._id] ?? [];
    }

    const scoreKpis = yield call(() =>
      GET_ALL_USER_KPIS(companyId, workspace._id),
    );

    let myKpis = null;
    const userScores = [];
    const sharedScores = [];

    scoreKpis.data.scores.forEach((score) => {
      if (score.scope === "User") {
        userScores.push(score);
      } else {
        sharedScores.push(...score.days);
      }
    });

    if (sharedScores.length) {
      scoreKpis.data.users.forEach((user) => {
        const idx = userScores.findIndex((u) => u.ownerId === user);
        if (idx >= 0) {
          userScores[idx].days.push(...sharedScores);
        } else {
          userScores.push({
            ownerId: user,
            days: [...sharedScores],
          });
        }
      });
    }

    // Company/Pacific time, not the viewer's browser clock
    const day = companyToday();
    const yesterday = companyToday().subtract(1, "days");
    const week = companyToday().startOf("week");
    const lastWeek = companyToday().subtract(1, "week").startOf("week");
    const month = companyToday().startOf("month");

    const kpiMetaById = {};
    (groupKpis || []).forEach((k) => {
      if (k?._id) kpiMetaById[String(k._id)] = k;
    });
    (Object.values(companyKpis || {}) || []).flat().forEach((k) => {
      if (k?._id) kpiMetaById[String(k._id)] = k;
    });

    const usersKpis = userScores?.map((userScore) => {
      const summary = {
        userId: userScore.ownerId,
        yesterday: { total: 0 },
        today: { total: 0 },
        week: { total: 0 },
        lastWeek: { total: 0 },
        month: { total: 0 },
      };

      for (let d = 0; d < userScore.days.length; d++) {
        const dayData = userScore.days[d];
        const dateRef = moment(dayData.day);
        const dayKey = dayData.day;

        if (dateRef >= day) {
          addKpiObjects(summary.today, dayData.kpis, kpiMetaById, dayKey);
        } else if (dateRef >= yesterday) {
          addKpiObjects(summary.yesterday, dayData.kpis, kpiMetaById, dayKey);
        }

        if (dateRef >= week) {
          addKpiObjects(summary.week, dayData.kpis, kpiMetaById, dayKey);
        } else if (dateRef >= lastWeek) {
          addKpiObjects(summary.lastWeek, dayData.kpis, kpiMetaById, dayKey);
        }
        if (dateRef >= month) {
          addKpiObjects(summary.month, dayData.kpis, kpiMetaById, dayKey);
        }
      }

      /* Closed Rate, Bid Accuracy, and AVG Ticket recompute via window-rates (isWindowRateKpi); everything else stays month-pinned. */
      const copyPinnedRates = (bucket) => {
        Object.entries(kpiMetaById).forEach(([id, meta]) => {
          if (!isRateLikeKpi(meta)) return;
          if (isWindowRateKpi(meta)) return;
          if ((Number(bucket[id]?.amt) || 0) > 0) return;
          const src = summary.month[id];
          if ((Number(src?.amt) || 0) <= 0) return;
          const { __day, ...rest } = src;
          bucket[id] = { ...rest };
        });
      };
      copyPinnedRates(summary.today);
      copyPinnedRates(summary.yesterday);
      copyPinnedRates(summary.week);
      copyPinnedRates(summary.lastWeek);

      if (summary.userId === userId) {
        myKpis = summary;
      }

      return summary;
    });

    // Closed Rate + Bid Accuracy for Today / Week / … (not month-pinned copy).
    if (workspace?._id && usersKpis.length) {
      yield call(applyShortWindowRates, {
        groupId: workspace._id,
        usersKpis,
        myKpis,
        myUserId: userId,
      });
    }

    // Average scores for admin view — every period bucket (same as useKpiData).
    const buildAdminBucket = (bucketName) => {
      const totals = { total: 0 };
      const teamScores = [];
      usersKpis.forEach((scores) => {
        const { total, ...rest } = scores[bucketName] || { total: 0 };
        if (total) teamScores.push(rest);
      });
      teamScores.forEach((scores) => {
        addKpiObjects(totals, scores, kpiMetaById);
      });
      Object.keys(totals).forEach((key) => {
        if (key !== "total") {
          const scorersCount = teamScores.reduce(
            (count, score) => count + (score[key] ? 1 : 0),
            0,
          );
          // 4 decimals only for Bid Accuracy (0.896 = 89.6%); 2 decimals would round it to a whole percent. Every other metric keeps 2.
          totals[key].amt = isBidAccuracyKpi(kpiMetaById[String(key)])
            ? Math.round(totals[key].amt * 10000) / 10000
            : Math.round(totals[key].amt * 100) / 100;
          totals[key].pts = Math.round(totals[key].pts * 100) / 100;
          totals[key].scorersCount = scorersCount;
        } else {
          totals.total = teamScores.length
            ? Math.round((totals.total / teamScores.length) * 100) / 100
            : 0;
        }
      });
      return totals;
    };

    yield put({
      type: types.GET_KPI_SCORE_SUCCESS,
      companyKpis,
      groupKpis,
      groupAllKpis,
      draftKpis,
      usersKpis: usersKpis.sort((a, b) => b.month.total - a.month.total),
      myKpis,
      adminKpis: {
        today: buildAdminBucket("today"),
        yesterday: buildAdminBucket("yesterday"),
        week: buildAdminBucket("week"),
        lastWeek: buildAdminBucket("lastWeek"),
        month: buildAdminBucket("month"),
      },
    });
  } catch (err) {
    yield put({ type: types.GET_KPI_SCORE_FAILED, error: err.message });
  }
}

function* GetPreviousKpis(action) {
  try {
    const { month, refresh } = action;
    let dateRef;
    if (!month) {
      dateRef = yield select(getKpiMonthRef);
    } else {
      dateRef = moment(month).format("YYYY-MM");
    }

    const prevKpis = yield select(getHistoricKpis);

    if (!refresh && prevKpis?.[dateRef]) {
      yield put({
        type: types.SET_TO_HISTORIC_KPIS,
        monthRef: dateRef,
      });
    } else {
      const companyId = yield select(getCompanyId);
      const workspace = yield select(getSelectedWorkspace);

      const scoreKpis = yield call(() =>
        GET_ALL_USER_KPIS(companyId, workspace._id, dateRef),
      );

      const userScores = [];
      const sharedScores = [];

      scoreKpis.data.scores.forEach((score) => {
        if (score.scope === "User") {
          userScores.push(score);
        } else {
          sharedScores.push(...score.days);
        }
      });

      if (sharedScores.length) {
        scoreKpis.data.users.forEach((user) => {
          const idx = userScores.findIndex((u) => u.ownerId === user);
          if (idx >= 0) {
            userScores[idx].days.push(...sharedScores);
          } else {
            userScores.push({
              ownerId: user,
              days: [...sharedScores],
            });
          }
        });
      }

      const groupKpis = yield select((state) => state.kpiReducer.groupKpis);
      const groupAllKpis = yield select((state) => state.kpiReducer.groupAllKpis);
      const kpiMetaById = {};
      (groupKpis || []).forEach((k) => {
        if (k?._id) kpiMetaById[String(k._id)] = k;
      });
      (groupAllKpis || []).forEach((k) => {
        if (k?._id) kpiMetaById[String(k._id)] = k;
      });

      const usersKpis = userScores.map((userData) => {
        const summary = {
          userId: userData.ownerId,
          month: { total: 0 },
        };

        for (let d = 0; d < userData.days.length; d++) {
          const dayData = userData.days[d];
          addKpiObjects(summary.month, dayData.kpis, kpiMetaById, dayData.day);
        }

        return summary;
      });

      const newKpiHistory = {};
      newKpiHistory[dateRef] = usersKpis.sort(
        (a, b) => b.month.total - a.month.total,
      );

      yield put({
        type: types.GET_PREVIOUS_KPIS_SUCCESS,
        historicKpis: newKpiHistory,
        monthRef: dateRef,
      });
    }
  } catch (err) {
    yield put({ type: types.GET_PREVIOUS_KPIS_FAILED, error: err.message });
  }
}

function* refreshKpisSaga() {
  yield takeEvery(types.REFRESH_KPIS, GetKpis);
  yield takeEvery(types.DELETE_USER_KPI_SUCCESS, RefreshKpis);
  yield takeEvery(types.UPDATE_USER_KPI_SUCCESS, RefreshKpis);
  yield takeEvery(types.ADD_USER_KPI_SUCCESS, RefreshKpis);
  yield takeEvery(types.UPDATE_GROUP_KPI_SUCCESS, RefreshKpis);
  yield takeEvery(types.DISABLE_GROUP_KPI_SUCCESS, RefreshKpis);
  yield takeEvery(types.CREATE_CUSTOM_GROUP_KPI_SUCCESS, RefreshKpis);
  yield takeEvery(types.ADD_INTEGRATION_SUCCESS, RefreshKpis);
  yield takeEvery(workspaceTypes.SET_SELECTED_WORKSPACE_SUCCESS, RefreshKpis);
  yield takeEvery(claimsType.PUT_USER_CLAIMS_SUCCESS, RefreshKpis);

  yield takeEvery(types.GET_PREVIOUS_KPIS_REQUEST, GetPreviousKpis);
  yield takeEvery(types.ADD_OLD_USER_KPI_SUCCESS, GetPreviousKpis, {
    refresh: true,
  });
}

function* AddKpi(action) {
  const { kpiId, userId, month, data } = action;
  const companyId = yield select(getCompanyId);
  const monthFormatted = month ? moment(month).format("YYYY-MM") : null;

  try {
    const result = yield call(() =>
      ADD_USER_KPI({
        kpiId,
        companyId,
        userId,
        data,
        month: monthFormatted,
      }),
    );

    if (result.error) {
      yield put({
        type: monthFormatted
          ? types.ADD_OLD_USER_KPI_FAILED
          : types.ADD_USER_KPI_FAILED,
        error: result.error.message,
      });
    } else {
      // Fetch current KPI scores so that we can instantly add new score
      const currentUsersKpis = yield select(getUsersKpis);
      const currentMyKpis = yield select(getMyKpis);

      // Deep copy KPI array of objects even if it's just the month parameter
      const usersKpis = cloneDeep(currentUsersKpis);
      let myKpis =  cloneDeep(currentMyKpis);
      const { amount, points } = result.data;

      const newScore = {};
      newScore[kpiId] = { amt: amount, pts: points };

      if(!myKpis) {
        myKpis = {
          userId,
          month: newScore,
        }
      } else if (myKpis.userId === userId) {
        addKpiObjects(myKpis.month, newScore);
      }

      const userIdx = usersKpis.findIndex((item) => item.userId === userId);
      if (userIdx >= 0) {
        addKpiObjects(usersKpis[userIdx].month, newScore);
      } else {
        usersKpis.push({
          userId,
          total: amount,
          month: newScore,
        });
      }

      yield put({
        type: monthFormatted
          ? types.ADD_OLD_USER_KPI_SUCCESS
          : types.ADD_USER_KPI_SUCCESS,
        usersKpis,
        myKpis,
      });
    }
  } catch (err) {
    yield put({ type: types.ADD_USER_KPI_FAILED, error: err.message });
  }
}

function* UpdateKpi(action) {
  const { id, kpiId, amount } = action;
  try {
    const result = yield call(() => UPDATE_USER_KPI(id, kpiId, amount));
    if (result.error) {
      yield put({
        type: types.UPDATE_USER_KPI_FAILED,
        error: result.error.message,
      });
    } else {
      yield put({ type: types.UPDATE_USER_KPI_SUCCESS });
    }
  } catch (err) {
    yield put({ type: types.UPDATE_USER_KPI_FAILED, error: err.message });
  }
}

function* DeleteKpi(action) {
  const { kpiId } = action;
  try {
    const result = yield call(() => DELETE_USER_KPI(kpiId));
    if (result.error) {
      yield put({
        type: types.DELETE_USER_KPI_FAILED,
        error: result.error.message,
      });
    } else {
      yield put({ type: types.DELETE_USER_KPI_SUCCESS });
    }
  } catch (err) {
    yield put({ type: types.DELETE_USER_KPI_FAILED, error: err.message });
  }
}

function* userKpiSaga() {
  yield takeEvery(types.ADD_USER_KPI_REQUEST, AddKpi);
  yield takeEvery(types.UPDATE_USER_KPI_REQUEST, UpdateKpi);
  yield takeEvery(types.DELETE_USER_KPI_REQUEST, DeleteKpi);
}

function* updateWorkspaceKpi(action) {
  const { groupKpi } = action;
  groupKpi.name = groupKpi.name ?? groupKpi.type;

  try {
    const result = yield call(() =>
      UPDATE_GROUP_KPI(groupKpi.groupId, groupKpi),
    );
    if (result.error) {
      yield put({
        type: types.UPDATE_GROUP_KPI_FAILED,
        error: result.error.message,
      });
    } else {
      yield put({ type: types.UPDATE_GROUP_KPI_SUCCESS });
    }
  } catch (err) {
    yield put({ type: types.UPDATE_GROUP_KPI_FAILED, error: err.message });
  }
}

function* createCustomWorkspaceKpi(action) {
  const { groupKpi } = action;

  try {
    const result = yield call(() => CREATE_CUSTOM_KPI(groupKpi));
    if (result.error) {
      yield put({
        type: types.CREATE_CUSTOM_GROUP_KPI_FAILED,
        error: result.error.message,
      });
    } else {
      yield put({ type: types.CREATE_CUSTOM_GROUP_KPI_SUCCESS });
    }
  } catch (err) {
    yield put({
      type: types.CREATE_CUSTOM_GROUP_KPI_FAILED,
      error: err.response?.message ?? err.message,
    });
  }
}

function* disableWorkspaceKpi(action) {
  const { id } = action;

  try {
    const result = yield call(() => DISABLE_GROUP_KPI(id));
    if (result.error) {
      yield put({
        type: types.DISABLE_GROUP_KPI_FAILED,
        error: result.error.message,
      });
    } else {
      yield put({ type: types.DISABLE_GROUP_KPI_SUCCESS });
    }
  } catch (err) {
    yield put({ type: types.DISABLE_GROUP_KPI_FAILED, error: err.message });
  }
}

function* addIntegration(action) {
  const { integration } = action;
  try {
    yield call(() => ADD_INTEGRATION(integration));
    yield put({ type: types.ADD_INTEGRATION_SUCCESS });
  } catch (err) {
    yield put({ type: types.ADD_INTEGRATION_FAILED, error: err.message });
  }
}

function* companyKpiSaga() {
  yield takeEvery(types.UPDATE_GROUP_KPI_REQUEST, updateWorkspaceKpi);
  yield takeEvery(types.DISABLE_GROUP_KPI_REQUEST, disableWorkspaceKpi);
  yield takeEvery(
    types.CREATE_CUSTOM_GROUP_KPI_REQUEST,
    createCustomWorkspaceKpi,
  );
  yield takeEvery(types.ADD_INTEGRATION_REQUEST, addIntegration);
}

function* rootSaga() {
  yield all([fork(refreshKpisSaga), fork(userKpiSaga), fork(companyKpiSaga)]);
}

/* Historic (Last Month / Past Boards) score fetching. The rest of the KPI
   layer is driven by hooks (useKpiData etc.), so the full kpi rootSaga above
   is not wired into the app's root saga. This minimal watcher is — it handles
   ONLY the historic fetch so Last Month leaderboard/KOTM populate, without
   double-firing REFRESH_KPIS against useKpiData. */
export function* historicKpisSaga() {
  yield takeEvery(types.GET_PREVIOUS_KPIS_REQUEST, GetPreviousKpis);
  yield takeEvery(types.ADD_OLD_USER_KPI_SUCCESS, GetPreviousKpis, {
    refresh: true,
  });
}

/* configureIntegration() (GoogleBusinessWindow's connect flow) dispatches
   ADD_INTEGRATION_REQUEST, which was never migrated to a hook like the rest
   of companyKpiSaga was — it was only ever handled by the rootSaga above,
   which isn't wired in, so the request silently never fired. Extracted as
   its own minimal watcher (same pattern as historicKpisSaga) rather than
   forking the whole companyKpiSaga, since that would also reactivate the
   KPI-update sagas the comment above says are superseded by hooks. */
export function* addIntegrationSaga() {
  yield takeEvery(types.ADD_INTEGRATION_REQUEST, addIntegration);
}

export default rootSaga;